import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    display: block;
    position: relative;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;

    &:hover,
    &:focus {
      outline: 0;

      .img {
        transform: scale(1.03);
      }

      &:before {
        opacity: 1;
      }
    }

    .img {
      position: relative;
      border-radius: 8px;
      transition: transform 0.4s ease;
    }

    &:before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, var(--green), transparent);
      opacity: 0.7;
      transition: opacity 0.3s ease;
      z-index: 1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['Python', 'SQL', 'Apache Spark', 'Apache Airflow', 'Apache Kafka', 'Databricks', 'Azure', 'AWS'];
  const Highlight = styled.span`
    background-color: rgba(121, 186, 193, 0.1); /* Low opacity accent */
    color: #79bac1;
    padding: 0 2px;
    border-radius: 4px;
  `;

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              As a <Highlight>Data Engineer</Highlight>, I love turning raw data into systems that actually solve real problems.
            </p>

            <p>
              My curiosity about how things work started with a love for math and
              computer science and naturally led me to the world of AI, where I quickly learned one thing:
              AI is only as strong as the data infrastructure behind it.
            </p>

            <p>
              Today, I design, build, and optimize data pipelines, AI/ML systems, and end-to-end platforms that turn raw data into actionable insights.
              I’m especially drawn to the intersection of AI and data infrastructure, constantly exploring how to make systems smarter, scalable, faster, and more reliable.
            </p>

            <p>Here are a few technologies I've been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
